import React from 'react';
import { RouteComponentProps } from '@reach/router';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';

import { RootState } from 'store/store';
import { IProviders, ITags } from 'store/providers/providerTypes';
import {
  getAllProviders,
  getAllProvidersFn,
  requireProviderTags,
  requireProviderTagsFn,
} from 'store/providers/providerSlice';
import { IAnalyseState, IQueryState, ReportOverride } from './ChartViewer';

import messages from './Messages';
import SurveyChart, { getFinalName } from './SurveyChart';
import { PrintInfo, Wrapper } from './styles';
import cloneDeep from 'lodash/cloneDeep';
import BubbleLoading from 'components/BubbleLoading';
import { smartDateDuration } from '../../utils/date';
import { appDispatch } from 'store/store';
import {
  ICheckPoint,
  IProviderSurveyReport,
  IQuestionReportField,
  IReportView,
  ISeries,
  ISurveyItem,
  ISurveys,
  ITagReportField,
  TimeScale,
} from 'store/survey/surveyTypes';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { IUsers } from 'store/users/usersType';
import { IQueryFilter } from 'store/entity/IQueryFilter';
import {
  requireAllSurveys,
  requireAllSurveysFn,
  requireCheckPoints,
  requireCheckPointsFn,
  requireSurvey,
  requireSurveyFn,
} from 'store/survey/surveySlice';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';

interface IStateToProps {
  tags: { [p: number]: ITags | null };
  surveys: ISurveys;
  thisUser: IThisUserState;
  providers: IProviders;
  users: IUsers['map'];
}

interface IDispatchToProps {
  getAllProviders: (
    providersId: number[]
  ) => ReturnType<typeof getAllProvidersFn>;
  requireProviderTags: (
    providerId: number
  ) => ReturnType<typeof requireProviderTagsFn>;
  requireSurvey: (
    surveyItem: ISurveyItem,
    forceReload?: boolean
  ) => ReturnType<typeof requireSurveyFn>;
  requireAllSurveys: (
    surveysTokens: string[]
  ) => ReturnType<typeof requireAllSurveysFn>;
  loadUsers: (userIds: number[]) => ReturnType<typeof requireSeveralUserFn>;

  requireCheckPoints: (
    surveyId?: number,
    reportId?: number,
    surveyToken?: string,
    ownerToken?: string,
    reportToken?: string
  ) => ReturnType<typeof requireCheckPointsFn>;
}

type IComponentProps = IStateToProps & IDispatchToProps & RouteComponentProps;

interface IComponentStates {
  analyse?: IAnalyseState;
  collapsedCharts?: number[];
  fields?: Array<ITagReportField | IQuestionReportField>;
  loading: boolean;
  providerId: number;
  providers?: number[];
  report?: IReportView;
  reportItem?: IProviderSurveyReport;
  requestQuery?: IQueryState;
  timeScale?: TimeScale;
  series?: ISeries[];
  users?: number[];
  showCheckPoints: boolean;
  surveyId?: number;
  surveyItem?: ISurveyItem;
  tags?: ITags;
  dummyVar?: boolean;
  extraContext?: string;
  reportOverride?: ReportOverride;
}

class PrintReport extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  constructor(props: IComponentProps) {
    super(props);
    const reportString = localStorage.getItem('printableReport');
    if (reportString) {
      localStorage.removeItem('printableReport');

      const {
        analyse,
        collapsedCharts,
        fields,
        providers,
        providerId,
        requestQuery,
        series,
        showCheckPoints,
        surveyItem,
        report,
        users,
        reportItem,
        timeScale,
        extraContext,
        reportOverride,
      } = JSON.parse(reportString);
      this.state = {
        analyse,
        collapsedCharts,
        fields,
        loading: true,
        providerId: Number(providerId),
        providers,
        requestQuery,
        series,
        users,
        showCheckPoints,
        surveyId: surveyItem ? surveyItem.surveyId : 0,
        surveyItem,
        report,
        reportItem,
        timeScale,
        extraContext,
        reportOverride,
      };
    } else {
      this.state = {
        analyse: undefined,
        requestQuery: undefined,
        loading: true,
        providerId: this.props.thisUser.provider.id!,
        showCheckPoints: false,
      };
    }
  }

  componentDidMount() {
    if (this.state.analyse) {
      this.props
        .requireProviderTags(this.state.providerId)
        .then(async () => {
          if (this.state.providers) {
            await this.props.getAllProviders(this.state.providers);
          }
          if (this.state.users) {
            await this.props.loadUsers(this.state.users);
          }
          return Promise.resolve();
        })
        .then(() => {
          if (this.state.surveyItem) {
            return this.props.requireSurvey(this.state.surveyItem);
          } else if (
            this.state.reportItem &&
            this.state.report &&
            this.state.report.surveyId &&
            this.state.reportItem.surveyReportLinks
          ) {
            const token = this.state.reportItem.surveyReportLinks.find(
              (link) => link.reportId === this.state.report!.id
            )!.surveyReportToken;
            return this.props.requireAllSurveys([token]);
          } else {
            return Promise.resolve();
          }
        })
        .then(() => {
          const { surveyItem } = this.state;
          if (surveyItem) {
            const surveyToken =
              surveyItem.editToken ||
              surveyItem.changeDataToken ||
              surveyItem.reportToken ||
              surveyItem.fillToken;
            const surveyId = surveyItem.surveyId;
            this.props.requireCheckPoints(surveyId, undefined, surveyToken);
          } else if (this.state.reportItem && this.state.report) {
            // when there is no provider survey item and it's a report for provider report item
            const reportId = this.state.report.id;
            const ownerToken = this.state.reportItem.ownerToken;
            const reportToken =
              this.state.reportItem.reportToken ||
              this.state.reportItem.selfReportToken;
            this.props.requireCheckPoints(
              undefined,
              reportId,
              undefined,
              ownerToken,
              reportToken
            );
          }
        })
        .then(() => {
          this.setState({ loading: false });
          // the next 6 lines is due to a bug that data labels of bar chart are not rendered at first render so we do the render twice
          setTimeout(() => {
            this.setState({ loading: true });
          }, 10);
          setTimeout(() => {
            this.setState({ loading: false });
          }, 100);
          // open print dialog
          setTimeout(window.print, 2000);
        });
    }
  }

  handleCollapseOrExpand = (type: 'collapse' | 'expand', reportId: number) => {
    let collapsedCharts = cloneDeep(this.state.collapsedCharts as number[]);
    if (type === 'collapse') {
      collapsedCharts.push(reportId);
    } else if (type === 'expand') {
      collapsedCharts = collapsedCharts.filter((id) => id !== reportId);
    }
    this.setState({ collapsedCharts });
  };

  render() {
    const {
      analyse,
      collapsedCharts,
      fields,
      loading,
      providerId,
      requestQuery,
      timeScale,
      report,
      reportItem,
      series,
      showCheckPoints,
      surveyId,
      extraContext,
      reportOverride,
    } = this.state;
    const { providers, surveys, tags, users } = this.props;
    if (!analyse) {
      return (
        <PrintInfo>
          <span>{messages.thereIsNoReadyReportToPrint}</span>
        </PrintInfo>
      );
    }
    const checkpoints = Object.values(surveys.checkPoints.data).filter(
      (checkpoint: ICheckPoint) => {
        if (report && report.id) {
          return checkpoint.reportId === report.id;
        }
        return !checkpoint.reportId;
      }
    );

    let startDate: number | undefined = undefined;
    let endDate: number | undefined = undefined;
    if (requestQuery)
      requestQuery!['0'] &&
        requestQuery!['0'].filters.forEach((filter: IQueryFilter) => {
          if (filter.type === 'dateRangeFilter') {
            startDate = filter.start as number;
            endDate = filter.end as number;
          }
        });

    return (
      <div>
        <PrintInfo>
          <span>{messages.printWillStartAutomatically}</span>
        </PrintInfo>
        <Wrapper>
          {loading && report ? (
            <BubbleLoading />
          ) : (
            <>
              {report && (
                <div style={{ /*maxWidth: '20cm',*/ margin: 'auto' }}>
                  <h5 style={{ textAlign: 'center' }}>
                    {getFinalName(
                      report.name +
                        (extraContext && !report.name.includes(extraContext)
                          ? ` (${extraContext})`
                          : ''),
                      report,
                      timeScale || report.time.timeScale,
                      {
                        timeScale: timeScale || report.time.timeScale,
                        timeLength: report.time.timeLength,
                      }
                    )}
                    -{' '}
                    {smartDateDuration(
                      startDate && new Date(startDate),
                      endDate && new Date(endDate),
                      false,
                      false,
                      true,
                      timeScale || report.time.timeScale
                    )}
                  </h5>
                  <SurveyChart
                    analyse={analyse}
                    checkpoints={Object.values(checkpoints)}
                    collapsedCharts={collapsedCharts as number[]}
                    collapseOrExpand={this.handleCollapseOrExpand}
                    fields={
                      fields as Array<ITagReportField | IQuestionReportField>
                    }
                    loading={false}
                    providers={providers}
                    report={report}
                    reportTitle={report.name}
                    requestQuery={requestQuery}
                    series={series as ISeries[]}
                    showCheckPoints={showCheckPoints}
                    users={users}
                    survey={
                      reportItem && report.surveyId
                        ? surveys.data[surveyId!]
                        : surveys.data[surveyId!]
                    }
                    tags={tags[providerId]}
                    timeScale={timeScale}
                    reportItemMap={{}}
                    reportOverride={reportOverride}
                  />
                </div>
              )}
            </>
          )}
        </Wrapper>
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<
  IStateToProps,
  RouteComponentProps,
  RootState
> = (state) => {
  const surveys = state.survey;
  const tags = state.providers.tags;
  const providers = state.providers;
  const thisUser = state.thisUser;
  const users = state.users.map;

  return { surveys, tags, providers, thisUser, users };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  RouteComponentProps
> = () => {
  return {
    requireProviderTags: (providerId: number) =>
      appDispatch(requireProviderTags({ providerId })),
    requireSurvey: (providerSurveyItemData: ISurveyItem) =>
      appDispatch(
        requireSurvey({ providerSurveyItemData: providerSurveyItemData })
      ),
    requireAllSurveys: (surveysTokens: string[]) =>
      appDispatch(requireAllSurveys({ surveyTokens: surveysTokens })),
    getAllProviders: (providersId: number[]) =>
      appDispatch(getAllProviders({ providersId })),
    loadUsers: (userIds: number[]) =>
      appDispatch(requireSeveralUser({ usersList: userIds })),
    requireCheckPoints: (
      surveyId?: number,
      reportId?: number,
      surveyToken?: string,
      ownerToken?: string,
      reportToken?: string
    ) =>
      appDispatch(
        requireCheckPoints({
          surveyId,
          reportId,
          surveyToken,
          ownerToken,
          reportToken,
        })
      ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PrintReport);
